import {
  Check,
  CircleAlert,
  History,
  Info,
  Pencil,
  Plus,
  Settings2,
  Sparkles,
  TriangleAlert,
  Trash2,
} from 'lucide-react'
import { type FormEvent, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'

import {
  useCreatePortfolioAdvicePreset,
  useDeletePortfolioAdvicePreset,
  usePortfolioAdvicePresets,
  useUpdatePortfolioAdvicePreset,
} from '../api/bots'
import type { Alert, AlertSeverity, PortfolioAdviceFinding, PortfolioAdvisorPreset } from '../api/types'
import { ALERT_SEVERITY_CLASS, ALERT_SEVERITY_LABEL } from '../lib/alerts'
import { useAdvisor } from '../lib/advisor'
import { MarkdownEditor } from './MarkdownEditor'
import { useClickOutside } from '../lib/useClickOutside'
import { useConfirm } from './ConfirmDialog'
import { Modal } from './Modal'
import { useToast } from './Toast'

const PORTFOLIO_ADVICE_SEVERITY_ICON: Record<AlertSeverity, typeof Info> = {
  info: Info,
  warning: TriangleAlert,
  critical: CircleAlert,
}

/** Lo que deja `app/services/portfolio_advisor.py` en `Alert.context` para
 *  `kind === "portfolio_advice"` — ver `_RESPONSE_SCHEMA` en
 *  gemini_client.py. `context` llega tipado como `Record<string, unknown>`
 *  porque lo comparte cualquier tipo de alerta, así que se valida acá en
 *  vez de confiar a ciegas en la forma. */
export function parseAdviceContext(
  alert: Alert,
): { summary: string; findings: PortfolioAdviceFinding[]; presetLabel: string | null } | null {
  const context = alert.context
  if (typeof context.summary !== 'string' || !Array.isArray(context.findings)) return null
  return {
    summary: context.summary,
    findings: context.findings as PortfolioAdviceFinding[],
    presetLabel: typeof context.preset_label === 'string' ? context.preset_label : null,
  }
}

/** Botón + menú del asesor de balance de flota (app/services/
 *  portfolio_advisor.py) — una entrada por pregunta preconfigurada, más
 *  "Gestionar preguntas" al final. Reemplaza al botón único que sólo
 *  pedía siempre la misma pregunta fija. Mismo patrón de dropdown que
 *  `UserMenu.tsx` (montado siempre, `.open` anima entrada/salida). */
export function PortfolioAdviceMenu({
  onRequest,
  isPending,
  advisorOpen,
  onToggleAdvisor,
}: {
  onRequest: (presetId: number) => void
  isPending: boolean
  /** Si viene, el menú suma arriba la opción de abrir o cerrar el panel del asesor. */
  advisorOpen?: boolean
  onToggleAdvisor?: () => void
}) {
  const { data: presets } = usePortfolioAdvicePresets()
  const advisor = useAdvisor()
  const [open, setOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  return (
    <div className="advice-menu" ref={ref}>
      <button
        type="button"
        className="icon-button icon-button--neutral"
        onClick={() => setOpen((current) => !current)}
        disabled={isPending}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Pedir balance de flota al asesor de IA"
        title="Pedir balance de flota al asesor de IA"
      >
        <Sparkles size={16} aria-hidden="true" className={isPending ? 'icon-spin' : undefined} />
      </button>

      <div className={`dropdown advice-menu__dropdown${open ? ' open' : ''}`} role="menu">
        {onToggleAdvisor && (
          <>
            <button
              type="button"
              className="dropdown__item"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onToggleAdvisor()
              }}
            >
              {advisorOpen ? 'Cerrar panel del asesor' : 'Abrir panel del asesor'}
            </button>
            <div className="dropdown__divider" />
          </>
        )}
        {!presets?.length && <div className="dropdown__empty muted">Sin preguntas todavía</div>}
        {(presets ?? []).map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="dropdown__item"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onRequest(preset.id)
            }}
          >
            {preset.label}
          </button>
        ))}
        <div className="dropdown__divider" />
        <NavLink
          to="/alertas?kind=portfolio_advice"
          onClick={() => setOpen(false)}
          className="dropdown__item"
          role="menuitem"
        >
          <History size={14} aria-hidden="true" />
          Historial de recomendaciones
        </NavLink>
        <div className="dropdown__empty muted">Abrir respuestas en</div>
        <button
          type="button"
          className="dropdown__item"
          role="menuitemradio"
          aria-checked={advisor.displayMode === 'panel'}
          onClick={() => {
            setOpen(false)
            advisor.setDisplayMode('panel')
          }}
        >
          Panel lateral
          {advisor.displayMode === 'panel' && <Check size={14} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="dropdown__item"
          role="menuitemradio"
          aria-checked={advisor.displayMode === 'modal'}
          onClick={() => {
            setOpen(false)
            advisor.setDisplayMode('modal')
          }}
        >
          Ventana
          {advisor.displayMode === 'modal' && <Check size={14} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="dropdown__item"
          role="menuitem"
          onClick={() => {
            setOpen(false)
            setManageOpen(true)
          }}
        >
          <Settings2 size={14} aria-hidden="true" />
          Gestionar preguntas
        </button>
      </div>

      {manageOpen &&
        createPortal(
          <PortfolioAdvicePresetsModal onClose={() => setManageOpen(false)} />,
          document.body,
        )}
    </div>
  )
}

function PortfolioAdvicePresetForm({
  preset,
  onDone,
}: {
  preset?: PortfolioAdvisorPreset
  onDone: () => void
}) {
  const createPreset = useCreatePortfolioAdvicePreset()
  const updatePreset = useUpdatePortfolioAdvicePreset()
  const toast = useToast()
  const [label, setLabel] = useState(preset?.label ?? '')
  const [instructions, setInstructions] = useState(preset?.instructions ?? '')
  const fieldId = preset ? `preset-${preset.id}` : 'preset-new'

  const isPending = createPreset.isPending || updatePreset.isPending

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    try {
      if (preset) {
        await updatePreset.mutateAsync({ id: preset.id, label, instructions })
        toast.success(`Pregunta "${label}" actualizada.`)
      } else {
        await createPreset.mutateAsync({ label, instructions })
        toast.success(`Pregunta "${label}" creada.`)
      }
      onDone()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo guardar la pregunta')
    }
  }

  return (
    <form className="preset-form" onSubmit={handleSubmit}>
      <div className="field">
        <label htmlFor={`${fieldId}-label`}>Nombre</label>
        <input
          id={`${fieldId}-label`}
          type="text"
          required
          maxLength={80}
          placeholder="ej: Qué bot conviene pausar"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor={`${fieldId}-instructions`}>Pregunta</label>
        <MarkdownEditor
          id={`${fieldId}-instructions`}
          required
          maxLength={4000}
          rows={8}
          placeholder="Qué le pedís al modelo, en tus palabras — se le suma al estado de la flota, no hace falta repetirlo"
          value={instructions}
          onChange={setInstructions}
        />
      </div>
      <div className="confirm-dialog__actions">
        <button className="button button--ghost" type="button" onClick={onDone}>
          Cancelar
        </button>
        <button className="button" type="submit" disabled={isPending}>
          {preset ? 'Guardar' : 'Crear'}
        </button>
      </div>
    </form>
  )
}

function PortfolioAdvicePresetsModal({ onClose }: { onClose: () => void }) {
  const { data: presets, isPending: loading } = usePortfolioAdvicePresets()
  const deletePreset = useDeletePortfolioAdvicePreset()
  const confirm = useConfirm()
  const toast = useToast()

  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  async function handleDelete(preset: PortfolioAdvisorPreset) {
    const ok = await confirm({
      title: 'Borrar pregunta',
      description: `Se va a borrar "${preset.label}".${
        preset.is_default
          ? ' Es la que usa el barrido automático de los lunes — sin ella, ese barrido te va a saltear hasta que marques otra.'
          : ''
      }`,
      confirmLabel: 'Borrar',
      tone: 'danger',
    })
    if (!ok) return
    try {
      await deletePreset.mutateAsync(preset.id)
      toast.success(`Pregunta "${preset.label}" borrada.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo borrar la pregunta')
    }
  }

  return (
    <Modal onClose={onClose} className="modal--presets">
      <h3>Preguntas del asesor de flota</h3>
      <p className="muted">
        Cada una es una pregunta puntual que se le manda al modelo junto con el estado de la
        flota — la marcada "default" es la que usa el barrido automático de los lunes
        (docs/plan_bot_trading.md §6.9).
      </p>

      {loading ? (
        <p className="muted">Cargando…</p>
      ) : (
        <ul className="preset-list">
          {(presets ?? []).map((preset) =>
            editingId === preset.id ? (
              <li key={preset.id} className="preset-list__row">
                <PortfolioAdvicePresetForm preset={preset} onDone={() => setEditingId(null)} />
              </li>
            ) : (
              <li key={preset.id} className="preset-list__row">
                <div className="preset-list__text">
                  <div className="preset-list__label">
                    {preset.label}
                    {preset.is_default && <span className="badge">default</span>}
                  </div>
                  <p className="preset-list__instructions muted">{preset.instructions}</p>
                </div>
                <div className="preset-list__actions">
                  <button
                    type="button"
                    className="icon-button icon-button--neutral"
                    onClick={() => setEditingId(preset.id)}
                    aria-label={`Editar "${preset.label}"`}
                    title="Editar"
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--neutral"
                    onClick={() => handleDelete(preset)}
                    disabled={deletePreset.isPending}
                    aria-label={`Borrar "${preset.label}"`}
                    title="Borrar"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      {creating ? (
        <PortfolioAdvicePresetForm onDone={() => setCreating(false)} />
      ) : (
        <button type="button" className="button button--ghost" onClick={() => setCreating(true)}>
          <Plus size={14} aria-hidden="true" />
          Nueva pregunta
        </button>
      )}

      <div className="confirm-dialog__actions">
        <button className="button" type="button" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </Modal>
  )
}

/** Los hallazgos de una lectura como tarjetas — mismas clases que ya usa
 *  `AlertsPage.tsx` para la vista de tarjetas del resto de las alertas
 *  (`strategy-grid`/`card card--plain strategy-card`/`badge-row`), así que
 *  esto no inventa un lenguaje visual nuevo, sólo le suma un ícono por
 *  severidad (no había un mapeo severidad→ícono para reusar). */
export function PortfolioAdviceFindingsView({
  summary,
  findings,
}: {
  summary: string
  findings: PortfolioAdviceFinding[]
}) {
  return (
    <>
      <p className="portfolio-advice-modal__summary">{summary}</p>
      {findings.length === 0 ? (
        <p className="muted">Sin hallazgos puntuales esta vez.</p>
      ) : (
        <div className="strategy-grid">
          {findings.map((finding, index) => {
            const Icon = PORTFOLIO_ADVICE_SEVERITY_ICON[finding.severity]
            return (
              // eslint-disable-next-line react/no-array-index-key -- sin id propio, no llegan de la API con uno
              <article key={index} className="card card--plain strategy-card finding-card">
                <div className="finding-card__meta">
                  <span className={ALERT_SEVERITY_CLASS[finding.severity]}>
                    <Icon size={12} aria-hidden="true" style={{ verticalAlign: '-2px' }} />{' '}
                    {ALERT_SEVERITY_LABEL[finding.severity]}
                  </span>
                  <span className="muted">{finding.type}</span>
                </div>
                <h4>{finding.title}</h4>
                <p className="muted">{finding.detail}</p>
              </article>
            )
          })}
        </div>
      )}
    </>
  )
}
